import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { submitLead } from "../lib/api";
import { CONTACT, SERVICES, BUDGETS } from "../lib/site";
import { ArrowRight, Loader2 } from "lucide-react";

const KIND_META = {
  consultation: {
    title: "Book a Consultation",
    description: "Tell us about your home and vision. We'll follow up to schedule a private consultation.",
  },
  quote: {
    title: "Request a Project Quote",
    description: "Share your project details and we'll prepare a tailored scope and estimate.",
  },
  plan: {
    title: "Submit Plans & Specs",
    description: "For builders and designers — send us project plans and we'll coordinate a low-voltage design.",
  },
  contact: {
    title: "Send a Message",
    description: "We read every message and reply personally.",
  },
};

const field =
  "w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-isav-green transition-colors";

export default function LeadDialog({
  kind = "consultation",
  defaultAudience = "",
  triggerClassName = "",
  triggerLabel,
  variant = "primary",
  testId = "open-lead-dialog",
}) {
  const meta = KIND_META[kind];
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    county: "",
    audience: defaultAudience,
    service_interest: "",
    budget_range: "",
    company: "",
    plan_link: "",
    project_details: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ kind, ...form });
      toast.success("Thank you — we'll be in touch shortly.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", county: "", audience: defaultAudience, service_interest: "", budget_range: "", company: "", plan_link: "", project_details: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const base =
    "group inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-500 uppercase tracking-[0.2em] transition-all duration-300 cursor-pointer";
  const styles = {
    primary: "bg-white text-isav-bg hover:bg-white/85",
    accent: "text-white hover:opacity-90 [background:linear-gradient(to_right,#3b82f6,#10b981)]",
    secondary: "border border-white/25 text-white hover:border-white",
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId} className={`${base} ${styles[variant]} ${triggerClassName}`}>
          {triggerLabel || meta.title}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg border border-white/10 bg-isav-bg2 text-white rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-400">{meta.title}</DialogTitle>
          <DialogDescription className="text-white/50 font-sans">{meta.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-5" data-testid="lead-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Name</Label>
              <Input data-testid="lead-name" className={field} value={form.name} onChange={set("name")} placeholder="Full name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Email</Label>
              <Input data-testid="lead-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Phone</Label>
              <Input data-testid="lead-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Optional" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">County</Label>
              <Select value={form.county} onValueChange={set("county")}>
                <SelectTrigger data-testid="lead-county" className={`${field} justify-between`}>
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent className="bg-isav-bg2 border-white/10 text-white rounded-none">
                  {CONTACT.serviceAreas.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {kind === "plan" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Company / Firm</Label>
                <Input data-testid="lead-company" className={field} value={form.company} onChange={set("company")} placeholder="Studio or company" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Plans Link</Label>
                <Input data-testid="lead-plan-link" className={field} value={form.plan_link} onChange={set("plan_link")} placeholder="Drive / Dropbox URL" />
              </div>
            </div>
          )}

          {kind !== "contact" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Primary Interest</Label>
              <Select value={form.service_interest} onValueChange={set("service_interest")}>
                <SelectTrigger data-testid="lead-service" className={`${field} justify-between`}>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-isav-bg2 border-white/10 text-white rounded-none">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(kind === "consultation" || kind === "quote") && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Project Budget Range</Label>
              <Select value={form.budget_range} onValueChange={set("budget_range")}>
                <SelectTrigger data-testid="lead-budget" className={`${field} justify-between`}>
                  <SelectValue placeholder="Select a budget range" />
                </SelectTrigger>
                <SelectContent className="bg-isav-bg2 border-white/10 text-white rounded-none">
                  {BUDGETS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Project Details</Label>
            <Textarea data-testid="lead-details" className={`${field} min-h-[90px]`} value={form.project_details} onChange={set("project_details")} placeholder="Tell us about your home and vision…" />
          </div>

          <button
            type="submit"
            data-testid="lead-submit"
            disabled={loading}
            className="group inline-flex w-full items-center justify-center gap-2 bg-white px-8 py-4 text-xs font-500 uppercase tracking-[0.2em] text-isav-bg transition-all hover:bg-white/85 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : meta.title}
            {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
